import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import api, { driverLogService, truckService } from '../api/api';
import { PRE_TRIP_TEMPLATE, POST_TRIP_TEMPLATE } from './inspectionTemplates';
import { confirmOdometerUpdate } from '../utils/odometer';
import { useAuth } from '../auth/AuthContext';
import { formatPortalDate } from '../utils/dateFormat';

function formatDateTime(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return formatPortalDate(d, { includeTime: true });
}

function toDateTimeLocalValue(value) {
  const d = value ? (value instanceof Date ? value : new Date(value)) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function browserPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000,
    });
  });
}

async function currentAddress() {
  const position = await browserPosition();
  const { latitude, longitude } = position.coords;
  const response = await api.get('/geolocation/reverse', {
    params: { lat: latitude, lon: longitude },
  });
  return response.data?.address || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

function labelType(type) {
  if (type === 'pre_trip') return 'Pre Trip';
  if (type === 'post_trip') return 'Post Trip';
  if (type === 'defect_report') return 'Defect Report';
  return type || '—';
}

function labelStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'resolved') return 'Resolved';
  if (s === 'pass') return 'Pass';
  if (s === 'fail') return 'Fail';
  if (s === 'needs_attention') return 'Needs Attention';
  return status || '—';
}

function statusChipProps(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'resolved') return { label: 'Resolved', color: 'success', variant: 'filled' };
  if (s === 'pass') return { label: 'Pass', color: 'success', variant: 'filled' };
  if (s === 'needs_attention') return { label: 'Needs Attention', color: 'warning', variant: 'filled' };
  if (s === 'fail') return { label: 'Fail', color: 'error', variant: 'filled' };
  return { label: labelStatus(status), color: 'default', variant: 'outlined' };
}

function yesNoNaLabel(state) {
  if (state === 'yes') return 'Yes';
  if (state === 'no') return 'No';
  if (state === 'na') return 'N/A';
  return '—';
}

function buildDefaultResponses(template) {
  const next = {};
  (template || []).forEach((section) => {
    (section.items || []).forEach((it) => {
      next[it.key] = { state: 'yes', note: '' };
    });
  });
  return next;
}

function calculateInspectionStatus(responses) {
  const values = Object.values(responses || {});
  return values.some((response) => response?.state === 'no') ? 'fail' : 'pass';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function buildReportHtml(inspection) {
  const details = inspection?.details || {};
  const sections = Array.isArray(details?.template) ? details.template : [];
  const responses = details?.responses && typeof details.responses === 'object' ? details.responses : {};

  const title = details?.title || labelType(inspection?.inspection_type) || 'Inspection';
  const subtitle = `${formatDateTime(inspection?.occurred_at)} • ${inspection?.inspected_by || '—'} • ${labelStatus(inspection?.status)}`;
  const makeModel = details?.vehicle_make_model || '';
  const plate = details?.license_plate || '';
  const location = details?.location_of_inspection || '';
  const jurisdiction = details?.jurisdiction || '';
  const otherDriver = details?.other_driver_name || details?.co_driver_name || '';
  const odometer = inspection?.odometer ?? '';
  const comments = details?.general_comments || '';
  const signature = details?.digital_signature || null;

  const rowsHtml = sections.map((section) => {
    const header = `<tr class="section"><td colspan="3">${escapeHtml(section.title || '')}</td></tr>`;
    const itemsHtml = (section.items || []).map((it) => {
      const resp = responses?.[it.key] || {};
      const state = yesNoNaLabel(resp.state);
      const note = resp.note || '';
      return `<tr>
        <td class="item">${escapeHtml(it.label || '')}</td>
        <td class="state">${escapeHtml(state)}</td>
        <td class="note">${escapeHtml(note || '—')}</td>
      </tr>`;
    }).join('');
    return header + itemsHtml;
  }).join('');

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      :root { --ink:#0b1b2a; --muted:#546e7a; --line:#e0e0e0; --bg:#ffffff; --chip:#f5f5f5; }
      body { font-family: Arial, Helvetica, sans-serif; margin: 24px; color: var(--ink); background: var(--bg); }
      .top { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; }
      h1 { margin:0; font-size: 20px; }
      .sub { margin-top:6px; color: var(--muted); font-size: 12px; }
      .meta { margin-top: 16px; border:1px solid var(--line); border-radius:12px; padding: 12px; }
      .meta-grid { display:grid; grid-template-columns: 1fr 1fr; gap:10px 16px; }
      .kv { display:flex; gap:10px; }
      .k { min-width: 150px; font-weight:bold; font-size: 12px; color: var(--muted); }
      .v { font-size: 12px; }
      table { width:100%; border-collapse: collapse; margin-top: 16px; }
      th, td { border:1px solid var(--line); padding: 8px; font-size: 12px; vertical-align: top; }
      th { background: #f7f7f7; text-align:left; }
      tr.section td { background:#fafafa; font-weight:bold; }
      td.state { width: 120px; font-weight:bold; }
      .comments { margin-top: 16px; border:1px solid var(--line); border-radius:12px; padding: 12px; }
      .comments h2 { margin:0 0 8px 0; font-size: 14px; }
      .comments pre { margin:0; white-space: pre-wrap; font-family: inherit; font-size: 12px; }
      .signature { margin-top:16px; border:1px solid var(--line); border-radius:12px; padding:12px; }
      .signature img { display:block; width:320px; max-width:100%; height:100px; object-fit:contain; object-position:left center; border-bottom:1px solid #90a4ae; }
      @media print {
        body { margin: 12mm; }
        .no-print { display:none !important; }
      }
    </style>
  </head>
  <body>
    <div class="top">
      <div>
        <h1>${escapeHtml(title)}</h1>
        <div class="sub">${escapeHtml(subtitle)}</div>
      </div>
      <div class="no-print" style="display:flex; gap:8px;">
        <button onclick="window.print()" style="padding:8px 10px; border:1px solid #cfd8dc; background:#fff; border-radius:10px; cursor:pointer;">Print / Save as PDF</button>
      </div>
    </div>

    <div class="meta">
      <div class="meta-grid">
        <div class="kv"><div class="k">Vehicle Make/Model</div><div class="v">${escapeHtml(makeModel)}</div></div>
        <div class="kv"><div class="k">License Plate #</div><div class="v">${escapeHtml(plate)}</div></div>
        <div class="kv"><div class="k">Location of Inspection</div><div class="v">${escapeHtml(location)}</div></div>
        <div class="kv"><div class="k">Jurisdiction</div><div class="v">${escapeHtml(jurisdiction)}</div></div>
        <div class="kv"><div class="k">Odometer</div><div class="v">${escapeHtml(odometer)}</div></div>
        <div class="kv"><div class="k">Other Driver</div><div class="v">${escapeHtml(otherDriver || '—')}</div></div>
      </div>
    </div>

    <table>
      <thead>
        <tr><th>Item</th><th>Working?</th><th>Comments/Notes</th></tr>
      </thead>
      <tbody>
        ${rowsHtml || `<tr><td colspan="3">No report details saved for this inspection.</td></tr>`}
      </tbody>
    </table>

    <div class="comments">
      <h2>General Comments</h2>
      <pre>${escapeHtml(comments || '—')}</pre>
    </div>
    ${signature?.image_data_url ? `<div class="signature">
      <strong>Driver Digital Signature</strong>
      <img src="${escapeHtml(signature.image_data_url)}" alt="Driver signature" />
      <div class="sub">Signed by ${escapeHtml(signature.signed_by || inspection?.inspected_by || '')} on ${escapeHtml(formatDateTime(signature.signed_at))}</div>
    </div>` : ''}
  </body>
</html>`;
}

function exportInspectionToPdf(inspection) {
  const html = buildReportHtml(inspection);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.left = '-9999px';
  iframe.style.top = '0';
  // Some browsers will print a blank page if the iframe has 0x0 dimensions.
  iframe.style.width = '1px';
  iframe.style.height = '1px';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.setAttribute('aria-hidden', 'true');

  const cleanup = () => {
    try { iframe.remove(); } catch { /* noop */ }
  };

  document.body.appendChild(iframe);

  const w = iframe.contentWindow;
  if (!w) { cleanup(); return; }
  const doc = w.document;

  // `srcdoc` can be unreliable for print in some environments; use doc.write.
  doc.open();
  doc.write(html);
  doc.close();

  const doPrint = () => {
    try { w.focus(); w.print(); } finally { setTimeout(cleanup, 1000); }
  };

  const startedAt = Date.now();
  const waitUntilReady = () => {
    const isReady = doc.readyState === 'complete' && doc.body && doc.body.textContent && doc.body.textContent.trim().length > 0;
    if (isReady) {
      const fontsReady = (doc.fonts && doc.fonts.ready) ? doc.fonts.ready : Promise.resolve();
      fontsReady.then(() => setTimeout(doPrint, 250));
      return;
    }
    if (Date.now() - startedAt > 5000) {
      setTimeout(doPrint, 250);
      return;
    }
    setTimeout(waitUntilReady, 50);
  };

  waitUntilReady();
}

export function InspectionReportDialog({ open, onClose, inspection, onEdit }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const details = inspection?.details || null;
  const sections = Array.isArray(details?.template) ? details.template : [];
  const responses = details?.responses && typeof details.responses === 'object' ? details.responses : {};
  const [inspectionPhotos, setInspectionPhotos] = useState([]);

  useEffect(() => {
    if (!open || inspection?.inspection_type !== 'defect_report' || !inspection?.truck_id || !inspection?.id) {
      setInspectionPhotos([]);
      return;
    }
    truckService.getInspectionPhotos(inspection.truck_id, inspection.id)
      .then((response) => setInspectionPhotos(Array.isArray(response.data) ? response.data : []))
      .catch(() => setInspectionPhotos([]));
  }, [open, inspection?.id, inspection?.inspection_type, inspection?.truck_id]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isPhone} PaperProps={{ sx: { borderRadius: isPhone ? 0 : 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {details?.title || labelType(inspection?.inspection_type) || 'Inspection'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {formatDateTime(inspection?.occurred_at)} • {inspection?.inspected_by || '—'} • {labelStatus(inspection?.status)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {onEdit && (
            <Button
              variant="contained"
              onClick={() => onEdit(inspection)}
              startIcon={<EditIcon />}
              sx={{ borderRadius: 2 }}
            >
              Edit
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => exportInspectionToPdf(inspection)}
            startIcon={<PrintIcon />}
            sx={{ borderRadius: 2 }}
          >
            Export PDF
          </Button>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Title" value={details?.title || labelType(inspection?.inspection_type)} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Inspection Date / Time" value={formatDateTime(inspection?.occurred_at)} InputProps={{ readOnly: true }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Vehicle Make/Model" value={details?.vehicle_make_model || ''} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="License Plate #" value={details?.license_plate || ''} InputProps={{ readOnly: true }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Location of Inspection" value={details?.location_of_inspection || ''} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Jurisdiction" value={details?.jurisdiction || ''} InputProps={{ readOnly: true }} />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField fullWidth size="small" label="Odometer" value={inspection?.odometer ?? ''} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth size="small" label="Inspected By" value={inspection?.inspected_by || ''} InputProps={{ readOnly: true }} />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField fullWidth size="small" label="Other Driver" value={details?.other_driver_name || details?.co_driver_name || ''} InputProps={{ readOnly: true }} />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField fullWidth size="small" label="Status" value={labelStatus(inspection?.status)} InputProps={{ readOnly: true }} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {inspection?.inspection_type === 'defect_report' && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${details?.defect_resolved ? '#9bd3b5' : '#ffcc80'}`, backgroundColor: details?.defect_resolved ? '#f0faf5' : '#fff8e1' }}>
                {details?.defect_resolved && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 900 }}>Defect resolved</Typography>
                    <Typography variant="body2">
                      {details?.defect_resolved_by_name ? `Resolved by ${details.defect_resolved_by_name}` : 'Marked complete'}
                      {details?.defect_resolved_at ? ` on ${formatDateTime(details.defect_resolved_at)}` : ''}
                    </Typography>
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label="Defects Observed"
                  value={inspection?.issues || details?.defects_observed || ''}
                  InputProps={{ readOnly: true }}
                  multiline
                  minRows={4}
                />
              </Paper>
            </Grid>
          )}

          {inspectionPhotos.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Attached Defect Photos</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                  {inspectionPhotos.map((photo) => (
                    <Box
                      key={photo.id}
                      component="a"
                      href={photo.url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ display: 'block', borderRadius: 2, overflow: 'hidden', border: '1px solid #cfd8dc', bgcolor: '#f8fafc' }}
                    >
                      <Box component="img" src={photo.url} alt={photo.original_name || 'Defect'} sx={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f7f7f7' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 160 }}>Working?</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Comments/Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sections.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                            No report details saved for this inspection.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sections.map((section) => (
                        <React.Fragment key={section.title}>
                          <TableRow>
                            <TableCell colSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                              {section.title}
                            </TableCell>
                          </TableRow>
                          {(section.items || []).map((it) => {
                            const resp = responses?.[it.key] || {};
                            return (
                              <TableRow key={it.key} hover>
                                <TableCell sx={{ maxWidth: 560 }}>{it.label}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={yesNoNaLabel(resp.state)}
                                    color={resp.state === 'yes' ? 'success' : resp.state === 'no' ? 'error' : 'default'}
                                    variant={resp.state === 'na' ? 'outlined' : 'filled'}
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                                  {resp.note || '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
              <TextField
                fullWidth
                label="General Comments"
                value={details?.general_comments || ''}
                InputProps={{ readOnly: true }}
                multiline
                minRows={3}
              />
            </Paper>
          </Grid>

          {details?.digital_signature?.image_data_url && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #90caf9', backgroundColor: '#f8fbff' }}>
                <Typography sx={{ fontWeight: 900, color: '#001f3f', mb: 1 }}>Driver Digital Signature</Typography>
                <Box
                  component="img"
                  src={details.digital_signature.image_data_url}
                  alt="Driver digital signature"
                  sx={{ display: 'block', width: { xs: '100%', sm: 360 }, maxHeight: 140, objectFit: 'contain', objectPosition: 'left center', bgcolor: '#fff', borderBottom: '1px solid #90a4ae' }}
                />
                <Typography variant="caption" color="text.secondary">
                  Signed by {details.digital_signature.signed_by || inspection?.inspected_by || '—'} on {formatDateTime(details.digital_signature.signed_at)}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export function AddInspectionDialog({
  open,
  onClose,
  onCreated,
  onUpdated,
  truckId,
  existingInspection = null,
  defaultInspectionType = 'pre_trip',
  lockInspectionType = false,
  hideTypeSelector = false,
  submitLabel = 'Add Inspection',
}) {
  const { user } = useAuth();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [inspectionType, setInspectionType] = useState(defaultInspectionType || 'pre_trip');
  const [occurredAt, setOccurredAt] = useState(() => toDateTimeLocalValue(new Date()));
  const [odometer, setOdometer] = useState('');
  const [inspectedBy, setInspectedBy] = useState('');
  const [otherDriver, setOtherDriver] = useState('');
  const [location, setLocation] = useState('');
  const [jurisdiction, setJurisdiction] = useState('Saskatchewan');
  const [generalComments, setGeneralComments] = useState('');
  const [didRefuel, setDidRefuel] = useState(false);
  const [fuelSource, setFuelSource] = useState('biomed');
  const [fuelAt, setFuelAt] = useState(() => toDateTimeLocalValue(new Date()));
  const [fuelType, setFuelType] = useState('Diesel');
  const [fuelQuantity, setFuelQuantity] = useState('');
  const [fuelCost, setFuelCost] = useState('');
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const [truck, setTruck] = useState(null);
  const isEditing = Boolean(existingInspection?.id);
  const isPostTrip = inspectionType === 'post_trip';
  const isTripInspection = inspectionType === 'pre_trip' || isPostTrip;
  const isAberdeenLocation = location.trim().toLowerCase().includes('aberdeen');
  const effectiveFuelSource = isPostTrip
    ? (isAberdeenLocation ? 'biomed' : 'outside')
    : fuelSource;

  const template = useMemo(() => (inspectionType === 'post_trip' ? POST_TRIP_TEMPLATE : PRE_TRIP_TEMPLATE), [inspectionType]);
  const calculatedStatus = useMemo(() => calculateInspectionStatus(responses), [responses]);

  useEffect(() => {
    if (!open) return;
    const details = existingInspection?.details || {};
    const nextType = existingInspection?.inspection_type || defaultInspectionType || 'pre_trip';
    setInspectionType(nextType);
    setOccurredAt(toDateTimeLocalValue(existingInspection?.occurred_at || new Date()));
    setOdometer(existingInspection?.odometer ?? '');
    setInspectedBy(user?.name || existingInspection?.inspected_by || '');
    setOtherDriver(details?.other_driver_name || details?.co_driver_name || '');
    setLocation(details?.location_of_inspection || '105 Industrial Drive, Aberdeen, Saskatchewan S0K0A0');
    setJurisdiction(details?.jurisdiction || 'Saskatchewan');
    setGeneralComments(details?.general_comments || '');
    const storedFuel = details?.pre_trip_fuel || {};
    setDidRefuel(Boolean(storedFuel?.did_refuel));
    setFuelSource(storedFuel?.fuel_source || 'biomed');
    setFuelAt(toDateTimeLocalValue(storedFuel?.fueled_at || new Date()));
    setFuelType(storedFuel?.fuel_type || 'Diesel');
    setFuelQuantity(storedFuel?.quantity_litres || '');
    setFuelCost(storedFuel?.total_cost || '');
    setResponses(details?.responses || {});
    setError('');
  }, [open, defaultInspectionType, existingInspection, user?.name]);

  useEffect(() => {
    if (!open || existingInspection?.id) return undefined;
    let cancelled = false;
    setLocating(true);
    currentAddress()
      .then((address) => {
        if (!cancelled) setLocation(address);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLocating(false);
      });
    return () => { cancelled = true; };
  }, [open, existingInspection?.id]);

  const useCurrentLocation = async () => {
    try {
      setLocating(true);
      setLocation(await currentAddress());
      setError('');
    } catch (locationError) {
      setError(locationError?.message || 'Could not fetch the current location.');
    } finally {
      setLocating(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setResponses((prev) => (prev && Object.keys(prev).length > 0 ? prev : buildDefaultResponses(template)));
  }, [open, template]);

  useEffect(() => {
    if (!open || !truckId) return;
    truckService.getById(truckId)
      .then((res) => {
        const nextTruck = res.data || null;
        setTruck(nextTruck);
        if (!isEditing) {
          setOdometer((value) => value || (nextTruck?.odometer == null ? '' : String(nextTruck.odometer)));
        }
      })
      .catch(() => setTruck(null));
  }, [open, truckId, isEditing]);

  const handleSave = async () => {
    if (!truckId) return;
    if (!inspectionType) { setError('Inspection type is required.'); return; }
    if (!occurredAt) { setError('Date/Time is required.'); return; }
    if (inspectionType === 'pre_trip' && !inspectedBy.trim()) { setError('Driver name is required for the pre-trip inspection.'); return; }
    try {
      setSaving(true);
      setError('');
      const normalizedResponses = { ...buildDefaultResponses(template), ...(responses || {}) };
      if (isTripInspection && didRefuel) {
        if (!fuelQuantity || Number(fuelQuantity) <= 0) {
          setError('Fuel quantity is required when pre-trip refuel is marked Yes.');
          setSaving(false);
          return;
        }
        if (effectiveFuelSource === 'outside' && (fuelCost === '' || Number(fuelCost) < 0)) {
          setError('Total cost is required for fuel filled outside Biomed.');
          setSaving(false);
          return;
        }
      }
      const details = {
        title: inspectionType === 'post_trip' ? 'Post-Trip Inspection' : 'Pre-Trip Inspection',
        location_of_inspection: location || null,
        jurisdiction: jurisdiction || null,
        other_driver_name: otherDriver.trim() || null,
        license_plate: truck?.license_plate || null,
        vehicle_make_model: [truck?.year, truck?.make, truck?.model].filter(Boolean).join(' ') || null,
        occurred_at_local: occurredAt,
        general_comments: generalComments || null,
        template: template.map((s) => ({ title: s.title, items: s.items.map((i) => ({ key: i.key, label: i.label })) })),
        responses: normalizedResponses,
        pre_trip_fuel: isTripInspection ? {
          did_refuel: didRefuel,
          fuel_source: didRefuel ? effectiveFuelSource : null,
          fueled_at: didRefuel && fuelAt ? new Date(fuelAt).toISOString() : null,
          odometer: didRefuel ? (odometer || null) : null,
          fuel_type: didRefuel ? fuelType : null,
          quantity_litres: didRefuel ? fuelQuantity : null,
          total_cost: didRefuel && effectiveFuelSource === 'outside' ? fuelCost : null,
        } : null,
      };

      const payload = {
        inspection_type: inspectionType,
        occurred_at: new Date(occurredAt).toISOString(),
        odometer: odometer || null,
        inspected_by: inspectedBy || null,
        status: calculatedStatus,
        issues: null,
        details,
      };

      if (!await confirmOdometerUpdate({
        currentValue: truck?.odometer,
        nextValue: payload.odometer,
        context: `update the vehicle odometer from this ${inspectionType === 'post_trip' ? 'post-trip' : 'pre-trip'} inspection`,
      })) {
        setSaving(false);
        return;
      }

      const res = isEditing
        ? await truckService.updateInspection(truckId, existingInspection.id, payload)
        : await truckService.createInspection(truckId, payload);
      if (!isEditing && isTripInspection && didRefuel) {
        await driverLogService.createFuelLog({
          truck_id: truckId,
          fueled_at: fuelAt ? new Date(fuelAt).toISOString() : new Date().toISOString(),
          odometer: odometer || null,
          quantity_litres: fuelQuantity,
          total_cost: effectiveFuelSource === 'outside' ? fuelCost : null,
          fuel_type: fuelType,
          fuel_source: effectiveFuelSource,
          station: effectiveFuelSource === 'biomed' ? 'Biomed' : 'Outside Biomed',
          notes: `Captured during ${isPostTrip ? 'post-trip' : 'pre-trip'} inspection.`,
        });
      }
      if (isEditing) {
        if (onUpdated) onUpdated(res.data);
      } else if (onCreated) {
        onCreated(res.data);
      }
      onClose();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || `Failed to ${isEditing ? 'update' : 'create'} inspection.`);
    } finally {
      setSaving(false);
    }
  };

  const setResponseState = (key, nextState) => {
    setResponses((prev) => {
      const current = prev?.[key] || { state: 'yes', note: '' };
      return { ...prev, [key]: { ...current, state: nextState } };
    });
  };

  const setResponseNote = (key, note) => {
    setResponses((prev) => {
      const current = prev?.[key] || { state: 'yes', note: '' };
      return { ...prev, [key]: { ...current, note } };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {isEditing ? 'Edit ' : ''}{inspectionType === 'post_trip' ? 'Post-Trip Inspection' : 'Pre-Trip Inspection'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {truck?.name || 'Truck'}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Title" value={inspectionType === 'post_trip' ? 'Post-Trip Inspection' : 'Pre-Trip Inspection'} InputProps={{ readOnly: true }} />
            </Grid>
            {!hideTypeSelector && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" disabled={lockInspectionType}>
                  <InputLabel>Inspection Type</InputLabel>
                  <Select value={inspectionType} label="Inspection Type" onChange={(e) => setInspectionType(e.target.value)}>
                    <MenuItem value="pre_trip">Pre Trip</MenuItem>
                    <MenuItem value="post_trip">Post Trip</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Location of Inspection"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                helperText="Auto-detected when permitted; editable. Address data © OpenStreetMap contributors."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Use current location"
                        title="Use Current Location"
                        onClick={useCurrentLocation}
                        disabled={locating}
                        edge="end"
                      >
                        {locating ? <CircularProgress size={18} /> : <MyLocationIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Vehicle Make/Model" value={[truck?.year, truck?.make, truck?.model].filter(Boolean).join(' ') || ''} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="License Plate #" value={truck?.license_plate || ''} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Jurisdiction" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Inspection Date / Time" value={formatDateTime(new Date(occurredAt))} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Odometer" value={odometer} onChange={(e) => setOdometer(e.target.value)} placeholder="e.g., 128000" />
            </Grid>
            {isTripInspection && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 3,
                    borderColor: didRefuel ? '#4caf50' : '#d9e6f2',
                    background: didRefuel
                      ? 'linear-gradient(135deg, #f0fff4 0%, #e8f5ff 100%)'
                      : 'linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalGasStationIcon color="success" />
                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>Did you fuel the vehicle?</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {isPostTrip
                            ? (effectiveFuelSource === 'biomed' ? 'Fuel at Biomed' : 'Fuel outside')
                            : 'If yes, fuel details are saved with this pre-trip and in Fuel Records.'}
                        </Typography>
                      </Box>
                    </Box>
                    <FormControlLabel
                      control={<Switch checked={didRefuel} onChange={(e) => setDidRefuel(e.target.checked)} />}
                      label={didRefuel ? 'Yes' : 'No'}
                    />
                  </Box>

                  {didRefuel && (
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" disabled={isPostTrip}>
                          <InputLabel>Fuel Location</InputLabel>
                          <Select value={effectiveFuelSource} label="Fuel Location" onChange={(e) => setFuelSource(e.target.value)}>
                            <MenuItem value="biomed">Fuel at Biomed</MenuItem>
                            <MenuItem value="outside">Fuel outside</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" type="datetime-local" label="Date & Time" InputLabelProps={{ shrink: true }} value={fuelAt} onChange={(e) => setFuelAt(e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField fullWidth size="small" label="Odometer" value={odometer} onChange={(e) => setOdometer(e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Fuel Type</InputLabel>
                          <Select value={fuelType} label="Fuel Type" onChange={(e) => setFuelType(e.target.value)}>
                            <MenuItem value="Diesel">Diesel</MenuItem>
                            <MenuItem value="Gasoline">Gasoline</MenuItem>
                            <MenuItem value="DEF">DEF</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField fullWidth required size="small" type="number" label="Fuel Quantity (litres)" inputProps={{ min: 0, step: '0.01' }} value={fuelQuantity} onChange={(e) => setFuelQuantity(e.target.value)} />
                      </Grid>
                      {fuelSource === 'outside' && (
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField fullWidth required size="small" type="number" label="Total Cost" inputProps={{ min: 0, step: '0.01' }} value={fuelCost} onChange={(e) => setFuelCost(e.target.value)} />
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Paper>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Inspected By" value={inspectedBy} InputProps={{ readOnly: true }} helperText="From your signed-in account" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Other Driver (optional)"
                value={otherDriver}
                onChange={(e) => setOtherDriver(e.target.value)}
                helperText="Use when two drivers complete the same pre/post-trip inspection."
              />
            </Grid>
          </Grid>
        </Paper>

        {isPhone ? (
          <Paper sx={{ mt: 2, borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            {(template || []).map((section) => (
              <Accordion key={section.title} disableGutters sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fafafa' }}>
                  <Typography sx={{ fontWeight: 900 }}>{section.title}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1.5, pb: 2 }}>
                  {(section.items || []).map((it) => {
                    const current = responses[it.key] || { state: 'yes', note: '' };
                    return (
                      <Paper
                        key={it.key}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 2, mb: 1.5, borderColor: 'rgba(0,0,0,0.12)' }}
                      >
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>
                          {it.label}
                        </Typography>

                        <ToggleButtonGroup
                          fullWidth
                          exclusive
                          value={current.state}
                          onChange={(_e, v) => { if (v) setResponseState(it.key, v); }}
                          sx={{
                            mb: 1.25,
                            '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800 },
                          }}
                        >
                          <ToggleButton value="yes">Yes</ToggleButton>
                          <ToggleButton value="no">No</ToggleButton>
                          <ToggleButton value="na">N/A</ToggleButton>
                        </ToggleButtonGroup>

                        <TextField
                          size="small"
                          fullWidth
                          label="Comments/Notes"
                          value={current.note || ''}
                          onChange={(e) => setResponseNote(it.key, e.target.value)}
                        />
                      </Paper>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        ) : (
          <Paper sx={{ mt: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f7f7f7' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 360 }}>Proper Working Order?</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Comments/Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {template.map((section) => (
                    <React.Fragment key={section.title}>
                      <TableRow>
                        <TableCell colSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                          {section.title}
                        </TableCell>
                      </TableRow>
                      {section.items.map((it) => {
                        const current = responses[it.key] || { state: 'yes', note: '' };
                        return (
                          <TableRow key={it.key} hover>
                            <TableCell sx={{ maxWidth: 520 }}>{it.label}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {['yes', 'no', 'na'].map((opt) => (
                                  <Button
                                    key={opt}
                                    size="small"
                                    variant={current.state === opt ? 'contained' : 'outlined'}
                                    onClick={() => setResponseState(it.key, opt)}
                                    sx={{ minWidth: 72, borderRadius: 2 }}
                                  >
                                    {opt === 'yes' ? 'Yes' : opt === 'no' ? 'No' : 'N/A'}
                                  </Button>
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={current.note || ''}
                                onChange={(e) => setResponseNote(it.key, e.target.value)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Paper sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="General Comments"
                value={generalComments}
                onChange={(e) => setGeneralComments(e.target.value)}
                multiline
                minRows={4}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: calculatedStatus === 'fail' ? '#ef9a9a' : '#a5d6a7',
                  bgcolor: calculatedStatus === 'fail' ? '#fff5f5' : '#f4fbf5',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Inspection Status (Automatic)
                </Typography>
                <Chip {...statusChipProps(calculatedStatus)} sx={{ fontWeight: 900 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {calculatedStatus === 'fail'
                    ? 'Failed automatically because one or more items were marked No / not working.'
                    : 'Passed automatically because no inspected item was marked No.'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : (isEditing ? <EditIcon /> : <AddIcon />)}>
          {saving ? 'Saving…' : (isEditing ? 'Save Changes' : submitLabel)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function DefectReportDialog({
  open,
  onClose,
  onSaved,
  truckId,
  existingInspection = null,
}) {
  const { user } = useAuth();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [truck, setTruck] = useState(null);
  const [occurredAt, setOccurredAt] = useState(() => toDateTimeLocalValue(new Date()));
  const [reportedBy, setReportedBy] = useState('');
  const [location, setLocation] = useState('');
  const [defects, setDefects] = useState('');
  const [severity, setSeverity] = useState('needs_attention');
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [recipientIds, setRecipientIds] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsError, setRecipientsError] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const isEditing = Boolean(existingInspection?.id);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setRecipientsLoading(true);
    setRecipientsError('');
    api.get('/defect-report-recipients').then(({ data }) => {
      if (cancelled) return;
      setRecipientOptions(data);
      setRecipientIds(existingInspection?.details?.email_recipient_user_ids
        ?? data.filter((account) => account.role === 'mechanic' || String(account.email || '').trim().toLowerCase() === 'agm@biomedwaste.com').map((account) => account.id));
    }).catch(() => {
      if (!cancelled) setRecipientsError('Could not load email recipients. Close and reopen the form to retry.');
    }).finally(() => { if (!cancelled) setRecipientsLoading(false); });
    return () => { cancelled = true; };
  }, [open, existingInspection]);

  useEffect(() => {
    if (!open) return;
    const details = existingInspection?.details || {};
    setOccurredAt(toDateTimeLocalValue(existingInspection?.occurred_at || new Date()));
    setReportedBy(user?.name || existingInspection?.inspected_by || '');
    setLocation(details?.location_of_observation || '');
    setDefects(existingInspection?.issues || details?.defects_observed || '');
    setSeverity(existingInspection?.status || 'needs_attention');
    setSelectedPhotos([]);
    setError('');
  }, [open, existingInspection, user?.name]);

  useEffect(() => {
    if (!open || existingInspection?.id) return undefined;
    let cancelled = false;
    setLocating(true);
    currentAddress()
      .then((address) => {
        if (!cancelled) setLocation(address);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLocating(false);
      });
    return () => { cancelled = true; };
  }, [open, existingInspection?.id]);

  const useCurrentLocation = async () => {
    try {
      setLocating(true);
      setLocation(await currentAddress());
      setError('');
    } catch (locationError) {
      setError(locationError?.message || 'Could not fetch the current location.');
    } finally {
      setLocating(false);
    }
  };

  useEffect(() => {
    if (!open || !existingInspection?.id || !truckId) {
      setExistingPhotos([]);
      return;
    }
    truckService.getInspectionPhotos(truckId, existingInspection.id)
      .then((response) => setExistingPhotos(Array.isArray(response.data) ? response.data : []))
      .catch(() => setExistingPhotos([]));
  }, [open, existingInspection?.id, truckId]);

  useEffect(() => {
    if (!open || !truckId) return;
    truckService.getById(truckId)
      .then((res) => setTruck(res.data || null))
      .catch(() => setTruck(null));
  }, [open, truckId]);

  const handleSave = async () => {
    if (!truckId) return;
    if (!defects.trim()) {
      setError('Describe the defect observed before saving.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = {
        inspection_type: 'defect_report',
        occurred_at: new Date(occurredAt || new Date()).toISOString(),
        odometer: null,
        inspected_by: reportedBy || null,
        status: severity || 'needs_attention',
        issues: defects.trim(),
        details: {
          ...(existingInspection?.details || {}),
          title: 'Truck Defect Report',
          email_recipient_user_ids: recipientIds,
          location_of_observation: location || null,
          license_plate: truck?.license_plate || null,
          vehicle_make_model: [truck?.year, truck?.make, truck?.model].filter(Boolean).join(' ') || null,
          defects_observed: defects.trim(),
        },
      };

      const res = isEditing
        ? await truckService.updateInspection(truckId, existingInspection.id, payload)
        : await truckService.createInspection(truckId, payload);
      if (selectedPhotos.length > 0) {
        await truckService.uploadInspectionPhotos(truckId, res.data.id, selectedPhotos);
      }
      onSaved?.(res.data);
      onClose();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || `Failed to ${isEditing ? 'update' : 'save'} defect report.`);
    } finally {
      setSaving(false);
    }
  };

  const removeExistingPhoto = async (photoId) => {
    if (!truckId || !existingInspection?.id) return;
    try {
      await truckService.deleteInspectionPhoto(truckId, existingInspection.id, photoId);
      setExistingPhotos((previous) => previous.filter((photo) => photo.id !== photoId));
    } catch (error) {
      setError(error?.response?.data?.error || error?.message || 'Failed to remove photo.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isPhone} PaperProps={{ sx: { borderRadius: isPhone ? 0 : 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {isEditing ? 'Edit Defect Report' : 'Report Truck Defect'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {truck?.name || 'Selected truck'}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!isEditing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Choose who receives this report by email. AGM and active mechanics are selected by default; you can change the selection.
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          {recipientsError && <Alert severity="error" sx={{ mb: 1 }}>{recipientsError}</Alert>}
          <Autocomplete
            multiple
            options={recipientOptions}
            loading={recipientsLoading}
            disabled={recipientsLoading || Boolean(recipientsError)}
            value={recipientOptions.filter((account) => recipientIds.includes(account.id))}
            onChange={(_event, selected) => setRecipientIds(selected.map((account) => account.id))}
            getOptionLabel={(account) => `${account.name || account.email} · ${account.email}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Email report to" placeholder="Search users by name or email" helperText={isEditing ? 'Editing saves recipients but does not resend email.' : 'Only selected users receive email. Clear all to save without sending email.'} />}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Observed Date / Time" value={formatDateTime(new Date(occurredAt))} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Reported By" value={reportedBy} InputProps={{ readOnly: true }} helperText="From your signed-in account" />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Where was it observed?"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              helperText="Auto-detected when permitted; editable. Address data © OpenStreetMap contributors."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Use current location"
                      title="Use Current Location"
                      onClick={useCurrentLocation}
                      disabled={locating}
                      edge="end"
                    >
                      {locating ? <CircularProgress size={18} /> : <MyLocationIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select value={severity} label="Severity" onChange={(e) => setSeverity(e.target.value)}>
                <MenuItem value="needs_attention">Needs Attention</MenuItem>
                <MenuItem value="fail">Unsafe / Do Not Use</MenuItem>
                <MenuItem value="pass">Informational</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Defects Observed"
              value={defects}
              onChange={(e) => setDefects(e.target.value)}
              multiline
              minRows={5}
              placeholder="Describe the defect, where it is on the truck, and any immediate action taken."
            />
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderStyle: 'dashed' }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Attach Photos</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Add up to 8 photos showing the defect. Phone users can take a photo directly.
              </Typography>
              <Button component="label" variant="outlined" startIcon={<PhotoCameraIcon />} sx={{ borderRadius: 2 }}>
                Add Photos
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  capture="environment"
                  multiple
                  onChange={(event) => setSelectedPhotos(Array.from(event.target.files || []).slice(0, 8))}
                />
              </Button>
              {(existingPhotos.length > 0 || selectedPhotos.length > 0) && (
                <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
                  {existingPhotos.map((photo) => (
                    <Box key={photo.id} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #cfd8dc' }}>
                      <Box component="img" src={photo.url} alt={photo.original_name || 'Defect'} sx={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeExistingPhoto(photo.id)}
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  {selectedPhotos.map((file) => (
                    <Box key={`${file.name}-${file.lastModified}`} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #90caf9' }}>
                      <Box component="img" src={URL.createObjectURL(file)} alt={file.name} sx={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || recipientsLoading || Boolean(recipientsError)} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <WarningAmberIcon />}>
          {saving ? 'Saving…' : (isEditing ? 'Save Changes' : 'Save Defect Report')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TruckInspectionLogs({ truckId, onInspectionCreated }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [type, setType] = useState('all');

  const params = useMemo(() => ({
    page,
    pageSize,
    q: q || undefined,
    type: type === 'all' ? undefined : type,
  }), [page, pageSize, q, type]);

  const load = async () => {
    if (!truckId) return;
    try {
      setLoading(true);
      setError('');
      const res = await truckService.getInspections(truckId, params);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || 'Failed to load inspections.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckId, params.page, params.pageSize, params.q, params.type]);

  const openEditInspection = (inspection) => {
    if (!inspection) return;
    setReportOpen(false);
    setSelectedInspection(null);
    setEditingInspection(inspection);
    setDialogOpen(true);
  };

  const closeInspectionDialog = () => {
    setDialogOpen(false);
    setEditingInspection(null);
  };

  return (
    <>
      <Paper sx={{ p: 2.5, borderRadius: 2, border: '2px solid #c62828', backgroundColor: '#ffebee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#c62828' }}>
            Inspection Logs
          </Typography>
          <Button variant="contained" onClick={() => { setEditingInspection(null); setDialogOpen(true); }} startIcon={<AddIcon />} sx={{ borderRadius: 2, backgroundColor: '#c62828', '&:hover': { backgroundColor: '#b71c1c' } }}>
            Add Inspection
          </Button>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select value={type} label="Type" onChange={(e) => { setPage(0); setType(e.target.value); }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pre_trip">Pre Trip</MenuItem>
              <MenuItem value="post_trip">Post Trip</MenuItem>
              <MenuItem value="defect_report">Defect Reports</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search"
            value={q}
            onChange={(e) => { setPage(0); setQ(e.target.value); }}
            placeholder="Search issues / inspector / status"
            sx={{ flex: 1, minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Per Page</InputLabel>
            <Select value={pageSize} label="Per Page" onChange={(e) => { setPage(0); setPageSize(parseInt(String(e.target.value), 10)); }}>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={150}>150</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Paper sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date/Time</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Odometer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Inspected By</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Issues</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading…</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="textSecondary">No matching records found.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => { setSelectedInspection(row); setReportOpen(true); }}
                      sx={{ cursor: 'pointer', ...(String(row.status || '').toLowerCase() === 'fail'
                        ? { backgroundColor: 'rgba(211, 47, 47, 0.06)' }
                        : String(row.status || '').toLowerCase() === 'needs_attention'
                          ? { backgroundColor: 'rgba(245, 124, 0, 0.06)' }
                          : {}) }}
                    >
                      <TableCell sx={{ fontWeight: 'bold' }}>{labelType(row.inspection_type)}</TableCell>
                      <TableCell>{formatDateTime(row.occurred_at)}</TableCell>
                      <TableCell>{row.odometer ?? '—'}</TableCell>
                      <TableCell>
                        {row.inspected_by || '—'}
                        {row?.details?.other_driver_name || row?.details?.co_driver_name ? (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            Other: {row.details.other_driver_name || row.details.co_driver_name}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.issues || '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" sx={{ fontWeight: 'bold' }} {...statusChipProps(row.status)} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="textSecondary">
            Page {page + 1} of {Math.max(1, Math.ceil(total / pageSize))}
          </Typography>
          <Button
            variant="outlined"
            disabled={page <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            disabled={(page + 1) * pageSize >= total}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
      </Paper>

      <AddInspectionDialog
        open={dialogOpen && editingInspection?.inspection_type !== 'defect_report'}
        onClose={closeInspectionDialog}
        truckId={truckId}
        existingInspection={editingInspection}
        onCreated={(inspection) => {
          setPage(0);
          load();
          onInspectionCreated?.(inspection);
        }}
        onUpdated={(inspection) => {
          setPage(0);
          load();
          onInspectionCreated?.(inspection);
        }}
      />
      <DefectReportDialog
        open={dialogOpen && editingInspection?.inspection_type === 'defect_report'}
        onClose={closeInspectionDialog}
        truckId={truckId}
        existingInspection={editingInspection}
        onSaved={(inspection) => {
          setPage(0);
          load();
          onInspectionCreated?.(inspection);
        }}
      />

      <InspectionReportDialog
        open={reportOpen}
        onClose={() => { setReportOpen(false); setSelectedInspection(null); }}
        inspection={selectedInspection}
        onEdit={openEditInspection}
      />
    </>
  );
}
